/**
 * WHAT THIS PERSON MAY SEE, AS THE SCREEN NEEDS TO KNOW IT.
 *
 * The organization's visibility settings, read straight from Firestore the way
 * the library and the policy page read policies. Every member may read the
 * settings collection, and these values are not secret: they say who sees
 * evidence, not what the evidence is.
 *
 * THIS IS NOT A SECURITY BOUNDARY AND MUST NEVER BE TREATED AS ONE. The server
 * enforces every one of these: the approval callables refuse the record and
 * strip comments and committee evidence before returning, the file callable
 * refuses an earlier version's document, the report callables refuse the
 * outstanding list, and Firestore rules refuse the version documents and the
 * retired policies themselves. This exists so the page does not offer a button
 * for a call that will be refused, and does not hide something the server
 * would have served, which is the failure the policy capabilities module was
 * written to stop.
 *
 * The fallback when the document is missing is LEGACY, not the recommendation,
 * exactly as it is on the server: between a deploy and the one-time
 * initializer an organization has no document, and it must behave as it did.
 */

import { useQuery } from '@tanstack/react-query';
import { doc, getDoc, getFirestore } from 'firebase/firestore';
import { useAuth } from '../../auth/useAuth';
import { effectiveSection, mayView, type VisibilitySettings } from '../admin/orgGovernance';

export function useVisibilitySettings() {
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;

  return useQuery({
    queryKey: ['org-visibility', orgId],
    enabled: Boolean(orgId),
    // A change has to reach an open tab without a reload, and the document is
    // tiny. Five minutes is long enough to keep it off every render.
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<VisibilitySettings> => {
      const snap = await getDoc(doc(getFirestore(), 'orgs', orgId!, 'settings', 'visibility'));
      return effectiveSection('visibility', snap.exists() ? snap.data() : undefined) as unknown as VisibilitySettings;
    },
  });
}

export interface Visible {
  approvalRecord: boolean;
  approvalComments: boolean;
  committeeEvidence: boolean;
  versionHistory: boolean;
  retiredPolicies: boolean;
  acknowledgementReports: boolean;
  /** False until the settings have arrived; nothing is hidden meanwhile. */
  known: boolean;
}

/**
 * The six answers for the signed-in person.
 *
 * While the settings are loading every answer is the LEGACY one, so a page
 * never flashes a section away and back. The server is what refuses, so the
 * worst an optimistic moment costs is a request that returns nothing.
 */
export function useVisible(): Visible {
  const { session } = useAuth();
  const { data, isSuccess } = useVisibilitySettings();
  const roles = session?.roles ?? [];
  const v = data ?? (effectiveSection('visibility', undefined) as unknown as VisibilitySettings);

  return {
    approvalRecord: mayView(roles, v.approvalRecordViewRoles),
    approvalComments: mayView(roles, v.approvalCommentViewRoles),
    committeeEvidence: mayView(roles, v.committeeEvidenceViewRoles),
    versionHistory: mayView(roles, v.versionHistoryViewRoles),
    retiredPolicies: mayView(roles, v.retiredPolicyViewRoles),
    acknowledgementReports: mayView(roles, v.acknowledgementReportViewRoles),
    known: isSuccess,
  };
}
